import React, { useState } from 'react';
import { useTestList } from '@/hooks/queries/test/useList';
import { useTestCreate, useTestUpdate, useTestDelete } from '@/hooks/mutations/test';
import { Test, TestInput } from '@/types/tests';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, HelpCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Pagination } from '@/components/Pagination';

const TestsPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [moduleIdFilter, setModuleIdFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);

  const [formData, setFormData] = useState<TestInput>({
    moduleId: '',
    passingPercentage: 70,
    maxAttempts: 3,
    timeLimit: 30,
    shuffleQuestions: true,
    shuffleAnswers: true,
    translations: [
      { locale: 'uz', name: '' },
      { locale: 'ru', name: '' },
      { locale: 'en', name: '' },
    ],
  });

  const { data: testsData, isLoading, refetch } = useTestList({
    moduleId: moduleIdFilter || undefined,
    pageNumber: page,
    pageSize: 50,
  });

  const { mutate: createTest, isPending: isCreating } = useTestCreate();
  const { mutate: updateTest, isPending: isUpdating } = useTestUpdate();
  const { mutate: deleteTest } = useTestDelete();

  const handleOpenCreate = () => {
    setSelectedTest(null);
    setFormData({
      moduleId: moduleIdFilter || '',
      passingPercentage: 70,
      maxAttempts: 3,
      timeLimit: 30,
      shuffleQuestions: true,
      shuffleAnswers: true,
      translations: [
        { locale: 'uz', name: '' },
        { locale: 'ru', name: '' },
        { locale: 'en', name: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (test: Test) => {
    setSelectedTest(test);
    setFormData({
      moduleId: test.moduleId,
      passingPercentage: test.passingPercentage,
      maxAttempts: test.maxAttempts,
      timeLimit: test.timeLimit,
      shuffleQuestions: test.shuffleQuestions,
      shuffleAnswers: test.shuffleAnswers,
      translations: [
        { locale: 'uz', name: test.name || '' },
        { locale: 'ru', name: '' },
        { locale: 'en', name: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, name: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTest) {
      updateTest(
        {
          id: selectedTest.id,
          data: {
            passingPercentage: formData.passingPercentage,
            translations: formData.translations,
          },
        },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Test yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Test yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createTest(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi modul testi yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Test yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedTest) return;
    deleteTest(selectedTest.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Test o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedTest(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Test o'chirilmadi", variant: 'destructive' }),
    });
  };

  const testsList: Test[] = testsData?.data?.data || testsData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Modul Testlari</h1>
          <p className="text-sm text-slate-500 mt-1">Har bir modul oxirida o'tkaziladigan oraliq testlar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi test
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Input
              placeholder="Modul ID bo'yicha filter (UUID)..."
              value={moduleIdFilter}
              onChange={(e) => { setModuleIdFilter(e.target.value); setPage(1); }}
              className="h-10 bg-slate-50/50 border-slate-200 rounded-xl font-mono text-xs"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : testsList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <HelpCircle className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha modul testlari mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Test Nomi</th>
                  <th className="px-4 py-3.5">Modul ID</th>
                  <th className="px-4 py-3.5">O'tish Foizi</th>
                  <th className="px-4 py-3.5">Vaqt Limiti</th>
                  <th className="px-4 py-3.5">Urinishlar</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {testsList.map((test) => (
                  <tr key={test.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-slate-900">{test.name || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{test.moduleId}</td>
                    <td className="px-4 py-3 font-medium text-emerald-600">{test.passingPercentage}%</td>
                    <td className="px-4 py-3 text-slate-600">{test.timeLimit} daqiqa</td>
                    <td className="px-4 py-3 text-slate-600">{test.maxAttempts} marta</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(test)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedTest(test);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={testsData?.meta?.pagination ?? testsData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedTest ? 'Testni tahrirlash' : 'Yangi modul testi'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {!selectedTest && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Modul ID (UUID)</Label>
                <Input
                  placeholder="550e8400-e29b-41d4-a716-446655440000"
                  value={formData.moduleId}
                  onChange={(e) => setFormData({ ...formData, moduleId: e.target.value })}
                  className="rounded-xl font-mono text-xs"
                  required
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">O'tish foizi (%)</Label>
                <Input
                  type="number"
                  value={formData.passingPercentage}
                  onChange={(e) => setFormData({ ...formData, passingPercentage: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Vaqt (daqiqa)</Label>
                <Input
                  type="number"
                  value={formData.timeLimit || 30}
                  onChange={(e) => setFormData({ ...formData, timeLimit: Number(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Max urinishlar</Label>
                <Input
                  type="number"
                  value={formData.maxAttempts || 3}
                  onChange={(e) => setFormData({ ...formData, maxAttempts: Number(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                <Label className="text-xs font-medium text-slate-700">Savollarni aralashtirish</Label>
                <Switch
                  checked={formData.shuffleQuestions}
                  onCheckedChange={(checked) => setFormData({ ...formData, shuffleQuestions: checked })}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                <Label className="text-xs font-medium text-slate-700">Javoblarni aralashtirish</Label>
                <Switch
                  checked={formData.shuffleAnswers}
                  onCheckedChange={(checked) => setFormData({ ...formData, shuffleAnswers: checked })}
                />
              </div>
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli nomlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { name: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Test nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.name || ''}
                          onChange={(e) => handleTranslationChange(locale, e.target.value)}
                          placeholder="1-modul yakuniy testi"
                          className="rounded-xl"
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Testni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu test va uning barcha savollari o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default TestsPage;
